import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useRealtimeEvent } from "../contexts/RealtimeContext";
import { partnerToast } from "../lib/realtimeToast";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { CreateListDialog } from "../components/CreateListDialog";
import { OnThisDayWidget } from "../components/OnThisDayWidget";
import { SortableListCard } from "../components/SortableListCard";
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { toast } from "sonner";
import { Plus, Heart, Sparkles, Plane, Coffee, Mountain, Home, Camera, Trees, Star, Compass, Map, BookOpen, Flame, Anchor } from "lucide-react";

const ICON_MAP = { Plane, Heart, Coffee, Mountain, Home, Camera, Trees, Star, Compass, Map, Sparkles, BookOpen, Flame, Anchor };

export default function Dashboard() {
  const { user, partner } = useAuth();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lists");
      setLists(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Real-time: refresh when partner makes any change affecting lists or entry counts
  useRealtimeEvent(
    ["list:created", "list:updated", "list:deleted", "lists:reordered", "entry:created", "entry:deleted", "entry:updated"],
    (data, actorId, event) => {
      if (actorId && actorId !== user?.id) {
        partnerToast(event, partner?.name);
      }
      load();
    }
  );

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = lists.findIndex((l) => l.id === active.id);
    const newIdx = lists.findIndex((l) => l.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(lists, oldIdx, newIdx);
    setLists(next); // optimistic
    try {
      await api.post("/lists/reorder", { ordered_ids: next.map((l) => l.id) });
    } catch (err) {
      toast.error("Sortierung konnte nicht gespeichert werden");
      load(); // rollback to server truth
    }
  };

  const moveListBy = async (id, delta) => {
    const idx = lists.findIndex((l) => l.id === id);
    const target = idx + delta;
    if (idx < 0 || target < 0 || target >= lists.length) return;
    const next = arrayMove(lists, idx, target);
    setLists(next);
    try {
      await api.post("/lists/reorder", { ordered_ids: next.map((l) => l.id) });
    } catch (err) {
      toast.error("Sortierung konnte nicht gespeichert werden");
      load();
    }
  };

  const stats = useMemo(() => {
    const total = lists.reduce((s, l) => s + (l.entry_count || 0), 0);
    const completed = lists.reduce((s, l) => s + (l.completed_count || 0), 0);
    return { total, completed, lists: lists.length };
  }, [lists]);

  return (
    <Layout>
      {/* Hero */}
      <section className="mb-16">
        <div className="flex items-baseline justify-between flex-wrap gap-4 mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-text_secondary mb-3">Eure Bucketlist</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
              Hallo {user?.name},
              {partner ? (<><br />schön, dass <span className="italic text-terracotta">{partner.name}</span> dabei ist.</>) :
              (<><br /><span className="italic text-sage">träumt los.</span></>)}
            </h1>
          </div>
          <Button
            onClick={() => setOpen(true)}
            className="rounded-full bg-ink text-alabaster hover:bg-ink/90 px-6 py-6"
            data-testid="dashboard-create-list-button"
          >
            <Plus size={16} className="mr-2" /> Neue Liste
          </Button>
        </div>

        <div className="flex flex-wrap gap-4 sm:gap-8 mt-12 pt-8 border-t border-border">
          <div>
            <div className="font-serif text-3xl text-ink">{stats.lists}</div>
            <div className="text-xs uppercase tracking-[0.25em] text-text_secondary mt-1">Listen</div>
          </div>
          <div className="ml-2 sm:ml-12">
            <div className="font-serif text-3xl text-ink">{stats.total}</div>
            <div className="text-xs uppercase tracking-[0.25em] text-text_secondary mt-1">Wünsche</div>
          </div>
          <div className="ml-2 sm:ml-12">
            <div className="font-serif text-3xl text-terracotta">{stats.completed}</div>
            <div className="text-xs uppercase tracking-[0.25em] text-text_secondary mt-1">Erlebt</div>
          </div>
        </div>
      </section>

      {/* On This Day */}
      <OnThisDayWidget />

      {/* Lists Grid */}
      {loading ? (
        <div className="text-center text-text_secondary py-24">Lade Listen …</div>
      ) : lists.length === 0 ? (
        <EmptyState onCreate={() => setOpen(true)} />
      ) : (
        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={lists.map((l) => l.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger" data-testid="lists-grid">
              {lists.map((lst, idx) => (
                <SortableListCard
                  key={lst.id}
                  lst={lst}
                  IconComp={ICON_MAP[lst.icon] || Heart}
                  onMoveUp={(id) => moveListBy(id, -1)}
                  onMoveDown={(id) => moveListBy(id, +1)}
                  canMoveUp={idx > 0}
                  canMoveDown={idx < lists.length - 1}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <CreateListDialog open={open} onOpenChange={setOpen} onCreated={(l) => setLists((p) => [{ ...l, entry_count: 0, completed_count: 0 }, ...p])} />
    </Layout>
  );
}

const EmptyState = ({ onCreate }) => (
  <div className="text-center py-24 max-w-lg mx-auto">
    <div className="w-16 h-16 rounded-full bg-bone mx-auto flex items-center justify-center mb-6">
      <Sparkles size={22} strokeWidth={1.5} className="text-ink" />
    </div>
    <h2 className="font-serif text-3xl text-ink mb-3">Eure Reise beginnt hier</h2>
    <p className="text-text_secondary mb-8">
      Legt eure erste Liste an — Reisen, Date-Ideen, Jahresziele oder etwas ganz Persönliches.
    </p>
    <Button onClick={onCreate} className="rounded-full bg-ink text-alabaster hover:bg-ink/90 px-6 py-6" data-testid="empty-create-list-button">
      <Plus size={16} className="mr-2" /> Erste Liste erstellen
    </Button>
  </div>
);
